import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import MasterViewPage from "../common/MasterViewPage";
import StatusBadge from "../common/StatusBadge";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getDistricts } from "../../services/districtService";
import { getStations } from "../../services/stationService";
import { getStationUserById } from "../../services/stationUserService";
import { getSubDivisions } from "../../services/subDivisionService";
import {
  buildLookupMap,
  extractApiErrorMessage,
  formatMasterDate,
} from "../../utils/masterHelpers";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import "./Users.css";

const formatRole = (role) =>
  role
    ? role.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

const asArray = (value) => (Array.isArray(value) ? value : []);

export default function UserView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading: capabilitiesLoading, user: capabilityUser } =
    useCapabilities();
  const [districtLookup, setDistrictLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [stationLookup, setStationLookup] = useState({});
  const [subDivisionLookup, setSubDivisionLookup] = useState({});
  const [toast, setToast] = useState({
    message: "",
    open: false,
    severity: "error",
  });
  const [user, setUser] = useState(null);

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user: capabilityUser }),
    [capabilityUser]
  );
  const {
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedSubDivisionId,
  } = scopeAccess;

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      if (!id) {
        setToast({
          message: "User ID is missing.",
          open: true,
          severity: "error",
        });
        setUser(null);
        return;
      }

      const stationParams = fixedSubDivisionId
        ? {
            sub_division_id: fixedSubDivisionId,
            ...(fixedDistrictId ? { district_id: fixedDistrictId } : {}),
          }
        : fixedDistrictId
        ? { district_id: fixedDistrictId }
        : {};

      const [
        districtsResult,
        subDivisionsResult,
        stationsResult,
        selectedUserResult,
      ] = await Promise.allSettled([
        canSelectDistrict ? getDistricts() : Promise.resolve([]),
        canSelectSubDivision
          ? getSubDivisions(
              fixedDistrictId ? { district_id: fixedDistrictId } : {}
            )
          : Promise.resolve([]),
        canSelectStation ? getStations(stationParams) : Promise.resolve([]),
        getStationUserById(id),
      ]);

      const districts =
        districtsResult.status === "fulfilled" ? asArray(districtsResult.value) : [];
      const subDivisions =
        subDivisionsResult.status === "fulfilled"
          ? asArray(subDivisionsResult.value)
          : [];
      const stations =
        stationsResult.status === "fulfilled" ? asArray(stationsResult.value) : [];
      const selectedUser =
        selectedUserResult.status === "fulfilled" ? selectedUserResult.value : null;

      setDistrictLookup(buildLookupMap(districts));
      setSubDivisionLookup(buildLookupMap(subDivisions));
      setStationLookup(buildLookupMap(stations));

      if (!selectedUser) {
        setToast({
          message: "User not found.",
          open: true,
          severity: "error",
        });
      }

      setUser(selectedUser || null);
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(apiError, "Failed to load user details."),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedSubDivisionId,
    id,
  ]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    loadData();
  }, [capabilitiesLoading, loadData]);

  const fields = useMemo(
    () =>
      user
        ? [
            { label: "Name", value: user.name || "-" },
            { label: "Username", value: user.username || "-" },
            { label: "Email", value: user.email || "-" },
            { label: "Role", value: formatRole(user.role) },
            {
              label: "District",
              value:
                user.district_name ||
                districtLookup[user.district_id] ||
                (user.district_id === capabilityUser?.district_id
                  ? capabilityUser?.district_name
                  : "-") ||
                "-",
            },
            {
              label: "Sub-division",
              value:
                user.sub_division_name ||
                subDivisionLookup[user.sub_division_id] ||
                (user.sub_division_id === capabilityUser?.sub_division_id
                  ? capabilityUser?.sub_division_name
                  : "-") ||
                "-",
            },
            {
              label: "Station",
              value: user.station_name || stationLookup[user.station_id] || "-",
            },
            { label: "Status", value: <StatusBadge value={user.is_active} /> },
            { label: "Last Login", value: formatMasterDate(user.last_login) },
          ]
        : [],
    [capabilityUser, districtLookup, stationLookup, subDivisionLookup, user]
  );

  return (
    <MasterShell pageClassName="users-module">
      {loading || capabilitiesLoading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading user</h2>
          <p className="masters-state__text">
            Please wait while the user details are being loaded.
          </p>
        </div>
      ) : user ? (
        <MasterViewPage
          description="User details with district, sub-division, and station names resolved from the related master tables."
          fields={fields}
          onBack={() => navigate("/users")}
          title="View User"
        />
      ) : (
        <div className="masters-state">
          <h2 className="masters-state__title">User not found</h2>
          <p className="masters-state__text">
            The selected user could not be found in the current response.
          </p>
        </div>
      )}

      <MasterToast
        message={toast.message}
        onClose={() =>
          setToast((currentToast) => ({ ...currentToast, open: false }))
        }
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
